import { useEffect, useState } from 'react';

import observer from 'saddlebag-observer';
import { isLoggedIn } from 'saddlebag-user-management';

import { useCookieBannerDismissed } from '../components/Header/hooks/useCookieBannerDismissed';

import { isBrowser } from './isEnv';

export const LOGIN_AUTO_PROMPT_OPENED = 'login-auto-prompt:opened';
export const LOGIN_AUTO_PROMPT_CLOSED = 'login-auto-prompt:closed';

export const publishLoginAutoPromptOpened = (): void => {
  observer.publish(LOGIN_AUTO_PROMPT_OPENED);
};

export const publishLoginAutoPromptClosed = (): void => {
  observer.publish(LOGIN_AUTO_PROMPT_CLOSED);
};

const AUTO_PROMPT_SETTLE_MS = 500;

const useIsLoginAutoPromptOpen = (): boolean => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isBrowser()) return undefined;

    const onOpen = () => setIsOpen(true);
    const onClose = () => setIsOpen(false);

    const openHandle = observer.subscribe(LOGIN_AUTO_PROMPT_OPENED, onOpen);
    const closeHandle = observer.subscribe(LOGIN_AUTO_PROMPT_CLOSED, onClose);

    return () => {
      observer.unsubscribe(openHandle);
      observer.unsubscribe(closeHandle);
    };
  }, []);

  return isOpen;
};

const useIsLoggedIn = (): boolean | null => {
  const [loggedIn, setLoggedIn] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isBrowser()) return undefined;
    let cancelled = false;

    const check = () => {
      isLoggedIn().then((result: boolean) => {
        if (!cancelled) setLoggedIn(result);
      });
    };

    check();
    const handle = observer.subscribe(LOGIN_AUTO_PROMPT_CLOSED, check);

    return () => {
      cancelled = true;
      observer.unsubscribe(handle);
    };
  }, []);

  return loggedIn;
};

const useAutoPromptHasSettled = (enabled: boolean): boolean => {
  const [hasSettled, setHasSettled] = useState(false);

  useEffect(() => {
    if (!isBrowser() || !enabled) return undefined;
    const timer = setTimeout(() => setHasSettled(true), AUTO_PROMPT_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [enabled]);

  return hasSettled;
};

type ShouldRenderGoogleOneTapState = {
  cookieBannerDismissed: boolean | null;
  loggedIn: boolean | null;
  enableLoginFormAutoPromptSEO: boolean;
  hasSettled: boolean;
  isAutoPromptOpen: boolean;
};

const shouldRenderGoogleOneTap = ({
  cookieBannerDismissed,
  enableLoginFormAutoPromptSEO,
  hasSettled,
  isAutoPromptOpen,
  loggedIn,
}: ShouldRenderGoogleOneTapState): boolean => {
  const cookiesAccepted = cookieBannerDismissed === true;
  const userLoggedOut = loggedIn === false;
  const seoPromptReady =
    !enableLoginFormAutoPromptSEO || (hasSettled && !isAutoPromptOpen);

  return cookiesAccepted && userLoggedOut && seoPromptReady;
};

type UseShouldRenderGoogleOneTapOptions = {
  enableLoginFormAutoPromptSEO: boolean;
  market: string | undefined;
};

export const useShouldRenderGoogleOneTap = ({
  enableLoginFormAutoPromptSEO,
  market,
}: UseShouldRenderGoogleOneTapOptions): boolean =>
  shouldRenderGoogleOneTap({
    isAutoPromptOpen: useIsLoginAutoPromptOpen(),
    cookieBannerDismissed: useCookieBannerDismissed(market),
    loggedIn: useIsLoggedIn(),
    hasSettled: useAutoPromptHasSettled(enableLoginFormAutoPromptSEO),
    enableLoginFormAutoPromptSEO,
  });
